const pushEventToDataLayer = (
  eventName: string,
  eventParams: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventParams
    })
  } else {
    console.warn('DataLayer não disponível')
  }
}

interface MenuHeaderParams {
  click_text: string
}

/**
 * Evento disparado quando o usuário clica em uma opção no menu do cabeçalho
 * @param params Parâmetros do evento
 */
export const gtmHeaderMenu = (params: MenuHeaderParams) => {
  pushEventToDataLayer('app_header_menu', params)
}

/**
 * Evento disparado quando o usuário clica em uma opção no menu do usuário
 * @param params Parâmetros do evento
 */
export const gtmHeaderMenuUsuario = (params: MenuHeaderParams) => {
  pushEventToDataLayer('app_header_menu_usuario', params)
}

/**
 * Evento disparado quando o usuário seleciona um ambiente
 * @param ambienteId ID do ambiente
 * @param ambienteNome Nome do ambiente
 * @param ambienteCategoria Categoria do ambiente (Diamante, Premium, etc.)
 * @param ambienteStatus Status do ambiente
 */
export const gtmTrackAmbienteSelecionado = (
  ambienteId: string | number,
  ambienteNome: string,
  ambienteCategoria: string,
  ambienteStatus: string | number
) => {
  const params = {
    environment_id: ambienteId,
    environment_name: ambienteNome || 'Ambiente sem nome',
    environment_category: ambienteCategoria || 'Categoria não definida',
    environment_status: String(ambienteStatus) || 'Status não definido'
  }

  pushEventToDataLayer('app_ambientes_seleciona_ambiente', params)
}

interface AmbienteMenuParams {
  environment_name?: string
  click_text: string
}

/**
 * Evento disparado quando o usuário clica em uma opção no menu de ambiente
 * @param params Parâmetros do evento
 */
export const gtmAmbienteMenu = (params: AmbienteMenuParams) => {
  const eventParams = {
    environment_name: params.environment_name || 'Ambiente não definido',
    click_text: params.click_text || 'Clique não definido'
  }

  pushEventToDataLayer('app_ambiente_menu', eventParams)
}

interface CupomParams {
  click_text: string
  off_value: string | number
}

/**
 * Evento disparado quando o usuário gera um cupom
 * @param params Parâmetros do evento
 */
export const gtmCuponsGerarCupom = (params: CupomParams) => {
  pushEventToDataLayer('app_cupons_gerar_cupom', params)
}

/**
 * Evento disparado quando o usuário visualiza um cupom
 * @param params Parâmetros do evento
 */
export const gtmCuponsVerCupom = (params: CupomParams) => {
  pushEventToDataLayer('app_cupons_ver_cupom', params)
}

export interface EcommerceItem {
  item_id: string
  item_name: string
  affiliation?: string
  coupon?: string
  currency?: string
  discount?: number
  index?: number
  item_brand?: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_list_id?: string
  item_list_name?: string
  item_variant?: string
  location_id?: string
  price: number
  quantity: number
}

/**
 * Converte um item da lista de produtos para o formato de item do GA4
 * @param item Item da lista de produtos
 * @param index Índice do item na lista
 * @param listName Nome da lista de produtos
 * @param listId ID da lista de produtos
 * @returns Item formatado para o GA4
 */
export const formatItemToGA4 = (
  item: any,
  index: number,
  listName: string,
  listId: string
): EcommerceItem => {
  return {
    item_id: item.itemListaProdutoId || item.id || `produto_${index}`,
    item_name: item.itemListaProdutoNome || item.nome || 'Produto sem nome',
    coupon: item.itemListaProdutoCupom || undefined,
    currency: 'BRL',
    discount: item.itemListaProdutoDesconto || 0,
    index: index,
    item_brand: item.itemListaProdutoLoja || undefined,
    item_category:
      item.categoriaNome || item.categoria || 'Categoria não definida',
    item_list_id: listId,
    item_list_name: listName,
    price: Number(item.itemListaProdutoPreco) || 0,
    quantity: item.itemListaProdutoQuantidade || 1
  }
}

/**
 * Converte uma lista de categorias em um array de itens no formato GA4
 * @param categorias Lista de categorias com itens
 * @param listName Nome da lista de produtos
 * @param listId ID da lista de produtos
 * @returns Array de itens formatados para o GA4
 */
export const formatCategoriesToItems = (
  categorias: any[],
  listName: string,
  listId: string
): EcommerceItem[] => {
  const items: EcommerceItem[] = []
  let globalIndex = 0

  const isCategoriaFormat = categorias.length > 0 && 'itens' in categorias[0]

  if (isCategoriaFormat) {
    categorias.forEach((categoria) => {
      if (categoria && categoria.itens && Array.isArray(categoria.itens)) {
        categoria.itens.forEach((item, index) => {
          const itemEnriquecido = {
            ...item,
            categoriaNome: categoria.nome
          }
          items.push(
            formatItemToGA4(itemEnriquecido, globalIndex, listName, listId)
          )
          globalIndex++
        })
      }
    })
  } else {
    categorias.forEach((item, index) => {
      items.push(formatItemToGA4(item, index, listName, listId))
    })
  }

  return items
}

/**
 * Dispara o evento view_item_list quando o usuário visualiza uma lista de produtos
 * @param items Array de itens no formato GA4
 * @param listName Nome da lista de produtos
 * @param listId ID da lista de produtos
 */
export const gtmViewItemList = (
  items: EcommerceItem[],
  listName: string,
  listId: string
) => {
  window.dataLayer?.push({ ecommerce: null })
  window.dataLayer?.push({
    event: 'view_item_list',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: items
    }
  })
}

/**
 * Dispara o evento select_item quando o usuário clica em um produto
 * @param item Item no formato GA4
 * @param listName Nome da lista de produtos
 * @param listId ID da lista de produtos
 */
export const gtmSelectItem = (
  item: EcommerceItem,
  listName: string,
  listId: string
) => {
  window.dataLayer?.push({ ecommerce: null })
  window.dataLayer?.push({
    event: 'select_item',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: [item]
    }
  })
}

interface StoriesEventParams {
  story_name: string
  story_type: number | string
}

interface StoriesCTAEventParams extends StoriesEventParams {
  click_text: string
  click_url: string
}

/**
 * Evento disparado quando o usuário abre um story
 * @param params Parâmetros do evento de story
 */
export const gtmStoriesOpen = (params: StoriesEventParams) => {
  pushEventToDataLayer('open_story', params)
}

/**
 * Evento disparado quando o usuário visualiza um story
 * @param params Parâmetros do evento de story
 */
export const gtmStoriesView = (params: StoriesEventParams) => {
  pushEventToDataLayer('view_story', params)
}

/**
 * Evento disparado quando o usuário clica no CTA de um story
 * @param params Parâmetros do evento de CTA de story
 */
export const gtmStoriesSelectCTA = (params: StoriesCTAEventParams) => {
  pushEventToDataLayer('select_cta_story', params)
}

declare global {
  interface Window {
    dataLayer?: any[]
  }
}
